import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Skeleton,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import MainLayout from '@/components/MainLayout';
import MemberDialog from '@/components/MemberDialog';
import TagPill from '@/components/TagPill';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { downloadCsv } from '@/lib/download';
import { toast } from '@/lib/toast';
import useNewParam from '@/lib/useNewParam';

const initials = (name) =>
  (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');

// Neutral columns; meaning is carried by the count pill, the 2px baseline
// (lime only on Converted) and per-card signals — not solid header fills.
const STAGES = [
  { id: 'new_lead', label: 'New' },
  { id: 'contacted', label: 'Contacted' },
  { id: 'trial_booked', label: 'Trial booked' },
  { id: 'converted', label: 'Converted', won: true },
];
const LOST_STAGE = { id: 'lost', label: 'Lost' };

const STALE_MS = 48 * 60 * 60 * 1000;

// The API exposes date_joined (record creation) but no stage-change
// timestamp, so aging is measured from when the lead was added. See NOTES.md.
const ageInfo = (lead) => {
  const since = lead.date_joined ? new Date(lead.date_joined) : null;
  if (!since || Number.isNaN(since.getTime())) return null;
  const ms = Date.now() - since.getTime();
  const days = Math.floor(ms / 86400000);
  const label =
    days >= 1
      ? `added ${days}d ago`
      : `added ${Math.max(1, Math.floor(ms / 3600000))}h ago`;
  const stale = ms > STALE_MS && (lead.lead_stage === 'new_lead' || lead.lead_stage === 'contacted');
  return { label, stale };
};

function LeadCard({ lead, won, onDragStart, onDragEnd, onClick }) {
  const age = ageInfo(lead);
  const interactive = Boolean(onDragStart);
  return (
    <Paper
      variant="outlined"
      draggable={interactive}
      onDragStart={interactive ? (e) => onDragStart(e, lead.member_id) : undefined}
      onDragEnd={onDragEnd}
      onClick={() => onClick?.(lead)}
      data-testid={`lead-card-${lead.member_id}`}
      sx={{
        p: 1.5,
        cursor: onClick ? 'pointer' : 'default',
        ...(won && {
          borderColor: (t) => alpha(t.palette.primary.main, 0.35),
          bgcolor: (t) => alpha(t.palette.primary.main, 0.05),
        }),
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <Stack direction="row" spacing={1.125} alignItems="center" sx={{ mb: 0.75 }}>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: '50%',
            flexShrink: 0,
            display: 'grid',
            placeItems: 'center',
            fontSize: 10.5,
            fontWeight: 600,
            bgcolor: (t) => (won ? t.palette.primary.main : alpha(t.palette.primary.main, 0.16)),
            color: (t) => (won ? t.palette.primary.contrastText : t.palette.primary.main),
          }}
        >
          {initials(lead.name)}
        </Box>
        <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
          {lead.name}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" noWrap display="block">
        {lead.email}
      </Typography>
      {lead.tags?.length > 0 && (
        <Stack direction="row" spacing={0.5} sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
          {lead.tags.map((tag, i) => (
            <TagPill key={i} tag={tag} />
          ))}
        </Stack>
      )}
      <Stack direction="row" spacing={0.625} alignItems="center" sx={{ mt: 1 }}>
        {won ? (
          <>
            <CheckCircleIcon sx={{ fontSize: 13, color: 'primary.main' }} />
            <Typography variant="caption" sx={{ fontWeight: 500, color: 'primary.main' }}>
              {lead.converted_at
                ? `won ${new Date(lead.converted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                : 'won'}
            </Typography>
          </>
        ) : age ? (
          <>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: age.stale ? 'warning.main' : 'success.main',
              }}
            />
            <Typography
              variant="caption"
              sx={{ fontWeight: 500, color: age.stale ? 'warning.main' : 'text.secondary' }}
            >
              {age.label}
              {age.stale ? ' — follow up' : ''}
            </Typography>
          </>
        ) : null}
      </Stack>
    </Paper>
  );
}

const LeadsPage = () => {
  const { isOwner } = useAuth();
  const [leads, setLeads] = useState([]);
  const [converted, setConverted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeLead, setActiveLead] = useState(null);
  const [convertTarget, setConvertTarget] = useState(null);
  const [converting, setConverting] = useState(false);
  // Suppress the click that some browsers fire right after a drag ends.
  const draggingRef = React.useRef(false);

  const fetchLeads = useCallback(async () => {
    try {
      const [leadsRes, convertedRes] = await Promise.all([
        api.get('/members', { params: { status: 'lead' } }),
        api.get('/members', { params: { status: 'active', lead_stage: 'converted' } }),
      ]);
      setLeads(leadsRes.data);
      // Recently won: keep the Converted column a celebration, not an archive.
      const cutoff = Date.now() - 30 * 86400000;
      setConverted(
        convertedRes.data.filter(
          (m) => m.converted_at && new Date(m.converted_at).getTime() >= cutoff,
        ),
      );
    } catch {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  useNewParam(() => openCreate());

  // Converted column shows real conversions (now active members) plus any
  // legacy still-lead rows in that stage; other columns are plain leads.
  const getLeadsByStage = (stageId) =>
    stageId === 'converted'
      ? [...converted, ...leads.filter((lead) => lead.lead_stage === 'converted')]
      : leads.filter((lead) => lead.lead_stage === stageId);

  const handleDragStart = (e, leadId) => {
    draggingRef.current = true;
    e.dataTransfer.setData('leadId', leadId);
  };

  const handleDragEnd = () => {
    // Clear on the next tick so a click event fired by the same gesture
    // still sees the flag.
    setTimeout(() => {
      draggingRef.current = false;
    }, 0);
  };

  const openEdit = (lead) => {
    if (draggingRef.current) return;
    setActiveLead(lead);
    setDialogOpen(true);
  };

  const openCreate = () => {
    setActiveLead(null);
    setDialogOpen(true);
  };

  const handleDrop = async (e, newStage) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData('leadId');
    if (newStage === 'converted') {
      const lead = leads.find((l) => l.member_id === leadId);
      if (lead) setConvertTarget(lead);
      return;
    }
    try {
      await api.patch(`/members/${leadId}`, { lead_stage: newStage });
      toast.success('Lead stage updated');
      fetchLeads();
    } catch {
      toast.error('Failed to update lead');
    }
  };

  const handleConvert = async () => {
    if (!convertTarget) return;
    setConverting(true);
    try {
      await api.post(`/members/${convertTarget.member_id}/convert`);
      toast.success(`${convertTarget.name} is now an active member`);
      setConvertTarget(null);
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to convert lead');
    } finally {
      setConverting(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const openCount = leads.filter((l) => l.lead_stage !== 'lost').length;
  const lostLeads = getLeadsByStage(LOST_STAGE.id);

  return (
    <MainLayout title="Leads" badge={loading ? undefined : `${openCount} open`}>
      <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
        <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mb: 2.25 }}>
          {isOwner && (
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<FileDownloadOutlinedIcon />}
              disabled={exporting}
              onClick={async () => {
                setExporting(true);
                try {
                  await downloadCsv('/members/export', 'leads', { status: 'lead' });
                } catch {
                  toast.error('Failed to export CSV');
                } finally {
                  setExporting(false);
                }
              }}
              data-testid="export-leads-button"
              sx={{ borderColor: 'divider', color: 'text.secondary' }}
            >
              {exporting ? 'Exporting…' : 'Export CSV'}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            data-testid="add-lead-button"
          >
            Add lead
          </Button>
        </Stack>

        <MemberDialog
          open={dialogOpen}
          mode={activeLead ? 'edit' : 'create'}
          variant="lead"
          member={activeLead}
          onClose={() => setDialogOpen(false)}
          onSaved={fetchLeads}
        />

        <Dialog open={Boolean(convertTarget)} onClose={() => setConvertTarget(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>Convert lead?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {convertTarget?.name} will become an active member and leave the lead pipeline. They&apos;ll
              appear under Members with a “converted-lead” tag.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConvertTarget(null)} disabled={converting}>
              Cancel
            </Button>
            <Button onClick={handleConvert} variant="contained" disabled={converting} data-testid="confirm-convert-button">
              {converting ? 'Converting…' : 'Convert to member'}
            </Button>
          </DialogActions>
        </Dialog>

        {loading ? (
          <Box sx={{ display: 'flex', gap: 1.75, flexDirection: { xs: 'column', md: 'row' } }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} variant="rounded" sx={{ flex: 1, height: { xs: 120, md: 400 } }} />
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              gap: 1.75,
              alignItems: 'flex-start',
              overflowX: 'auto',
              // Phones: stack the columns — horizontal kanban + HTML5 drag
              // don't work on touch. Stage changes happen via card tap → edit.
              flexDirection: { xs: 'column', md: 'row' },
            }}
            data-testid="kanban-board"
          >
            {STAGES.map((stage) => {
              const stageLeads = getLeadsByStage(stage.id);
              return (
                <Box
                  key={stage.id}
                  onDrop={(e) => handleDrop(e, stage.id)}
                  onDragOver={handleDragOver}
                  data-testid={`kanban-column-${stage.id}`}
                  sx={{
                    flex: { xs: '0 0 auto', md: '1 1 200px' },
                    minWidth: { xs: 0, md: 190 },
                    width: { xs: '100%', md: 'auto' },
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      px: 0.5,
                      pb: 1.25,
                      mb: 1.25,
                      borderBottom: 2,
                      borderColor: (t) =>
                        stage.won ? t.palette.primary.main : alpha(t.palette.text.primary, 0.18),
                    }}
                  >
                    <Typography variant="body2" fontWeight={600} color={stage.won ? 'primary.main' : 'text.primary'}>
                      {stage.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        px: 1,
                        py: 0.125,
                        borderRadius: 999,
                        bgcolor: (t) => (stage.won ? t.palette.primary.main : t.palette.action.hover),
                        color: (t) => (stage.won ? t.palette.primary.contrastText : t.palette.text.secondary),
                      }}
                    >
                      {stageLeads.length}
                    </Typography>
                  </Stack>
                  <Stack spacing={1} sx={{ flex: 1, minHeight: { xs: 0, md: 320 } }}>
                    {stageLeads.map((lead) => {
                      const isConvertedMember = lead.status !== 'lead';
                      return (
                        <LeadCard
                          key={lead.member_id}
                          lead={lead}
                          won={stage.won}
                          onDragStart={isConvertedMember ? undefined : handleDragStart}
                          onDragEnd={isConvertedMember ? undefined : handleDragEnd}
                          onClick={isConvertedMember ? undefined : openEdit}
                        />
                      );
                    })}
                    {stage.id === 'new_lead' && (
                      <Box
                        onClick={openCreate}
                        sx={{
                          border: 1,
                          borderStyle: 'dashed',
                          borderColor: 'divider',
                          borderRadius: 3,
                          py: 1.125,
                          textAlign: 'center',
                          cursor: 'pointer',
                          '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                        }}
                      >
                        <Typography variant="caption" fontWeight={500} color="text.secondary">
                          + Add lead
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>
              );
            })}

            <Box
              onDrop={(e) => handleDrop(e, LOST_STAGE.id)}
              onDragOver={handleDragOver}
              data-testid={`kanban-column-${LOST_STAGE.id}`}
              sx={{
                width: { xs: '100%', md: 170 },
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ px: 0.5, pb: 1.25, mb: 1.25, borderBottom: 2, borderColor: 'divider' }}
              >
                <Typography variant="body2" fontWeight={600} color="text.secondary">
                  {LOST_STAGE.label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    px: 1,
                    py: 0.125,
                    borderRadius: 999,
                    bgcolor: 'action.hover',
                    color: 'text.secondary',
                  }}
                >
                  {lostLeads.length}
                </Typography>
              </Stack>
              <Stack spacing={1} sx={{ flex: 1, minHeight: { xs: 0, md: 320 }, opacity: 0.6 }}>
                {lostLeads.map((lead) => (
                  <Paper
                    key={lead.member_id}
                    variant="outlined"
                    draggable
                    onDragStart={(e) => handleDragStart(e, lead.member_id)}
                    onDragEnd={handleDragEnd}
                    onClick={() => openEdit(lead)}
                    data-testid={`lead-card-${lead.member_id}`}
                    sx={{ p: 1.25, cursor: 'pointer' }}
                  >
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {lead.name}
                    </Typography>
                    {lead.tags?.length ? (
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
                        {lead.tags.map((tag, i) => (
                          <TagPill key={i} tag={tag} />
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {lead.email}
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Stack>
            </Box>
          </Box>
        )}
      </Box>
    </MainLayout>
  );
};

export default LeadsPage;
